/**
 * useVoiceSession - Hook that manages a Gemini Live voice session
 *
 * Wraps useGeminiLive with MCP tool support and transcript handling.
 * Voice transcripts are pushed into the parent's messageTexts via callbacks
 * so they appear as regular chat bubbles.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useGeminiLive } from "./useGeminiLive";
import GlobalVariables from "../models/globalVariables";
import { v4 as uuidv4 } from "uuid";

const GEMINI_LIVE_URL = GlobalVariables.getAPIURL() + "/api/gemini-live";

/**
 * Strip voice-useless fields from tool response data.
 * Removes URLs, metadata instructions, and internal hints
 * but keeps all actual content/analysis text intact.
 */
const STRIP_KEYS = new Set([
  "downloadURL", "directLink", "previewImageURL", "documentURL",
  "fileURL", "storageURL", "storagePath", "filePath", "thumbnailURL",
  "_groundingInstruction", "_documentLinkInstruction", "_contentSearchHint",
  "_pcState", "_emptyStateMessage", "_hint",
  "lazyLoadJsonFileKey", "guidelineAttachments",
]);

function cleanForVoice(data) {
  if (!data || typeof data !== "object") return data;
  if (Array.isArray(data)) return data.map(cleanForVoice);

  const cleaned = {};
  for (const [key, value] of Object.entries(data)) {
    if (STRIP_KEYS.has(key)) continue;
    cleaned[key] = cleanForVoice(value);
  }
  return cleaned;
}

const FRIENDLY_TOOL_MESSAGES = {
  get_documents_for_loan: "Looking up loan documents...",
  rag_search_documents: "Searching through documents...",
  get_tasks_for_loan: "Checking loan tasks...",
  get_task_details_for_loan: "Getting task details...",
  help_center_search: "Searching help center...",
  get_loan_details: "Fetching loan details...",
  get_borrower_info: "Looking up borrower information...",
  search_loans: "Searching loans...",
  get_pipeline_status: "Checking pipeline status...",
  get_processing_checklist: "Loading processing checklist...",
};

export function useVoiceSession({
  organizationId,
  loanId,
  uid,
  email,
  name,
  chatHistory,
  addUserMessage,
  addAssistantMessage,
  updateMessageById,
  replaceMessageById,
  finalizeMessageById,
  setActiveStreamingMessageId,
}) {
  const [toolStatus, setToolStatus] = useState(null);

  // Turn tracking refs
  const turnPhaseRef = useRef("idle"); // "idle" | "user_speaking" | "ai_speaking"
  const currentUserMsgIdRef = useRef(null);
  const currentAiMsgIdRef = useRef(null);
  const firstAiTextRef = useRef(true); // tracks whether next transcript replaces "Speaking..."

  // Stable refs for closures
  const organizationIdRef = useRef(organizationId);
  const loanIdRef = useRef(loanId);
  const uidRef = useRef(uid);
  const chatHistoryRef = useRef(chatHistory);
  organizationIdRef.current = organizationId;
  loanIdRef.current = loanId;
  uidRef.current = uid;
  chatHistoryRef.current = chatHistory;

  // Stable refs for callbacks (avoid stale closures in handleTranscript)
  const addUserMessageRef = useRef(addUserMessage);
  const addAssistantMessageRef = useRef(addAssistantMessage);
  const updateMessageByIdRef = useRef(updateMessageById);
  const replaceMessageByIdRef = useRef(replaceMessageById);
  const finalizeMessageByIdRef = useRef(finalizeMessageById);
  const setActiveStreamingMessageIdRef = useRef(setActiveStreamingMessageId);
  addUserMessageRef.current = addUserMessage;
  addAssistantMessageRef.current = addAssistantMessage;
  updateMessageByIdRef.current = updateMessageById;
  replaceMessageByIdRef.current = replaceMessageById;
  finalizeMessageByIdRef.current = finalizeMessageById;
  setActiveStreamingMessageIdRef.current = setActiveStreamingMessageId;

  /**
   * Handle MCP tool calls from Gemini
   */
  const handleToolCall = useCallback(async (functionCall) => {
    const friendlyMsg =
      FRIENDLY_TOOL_MESSAGES[functionCall.name] || `Running ${functionCall.name}...`;
    setToolStatus(friendlyMsg);

    try {
      const response = await fetch(`${GEMINI_LIVE_URL}/execute-tool`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          toolName: functionCall.name,
          args: functionCall.args,
          organizationId: organizationIdRef.current,
          userId: uidRef.current,
          loanId: loanIdRef.current,
        }),
      });

      const result = await response.json();
      setToolStatus(null);

      if (!result.success) {
        console.error(`Tool ${functionCall.name} failed:`, result.error);
        return { error: result.error || "Tool execution failed" };
      }

      const data = result.data || result;
      // Strip voice-useless fields (URLs, metadata) to reduce payload size
      // while keeping all actual content for Gemini to reason about.
      return cleanForVoice(data);
    } catch (err) {
      console.error(`Error calling tool ${functionCall.name}:`, err);
      setToolStatus(null);
      return { error: err.message };
    }
  }, []);

  /**
   * Handle transcript events from Gemini.
   * Creates/updates messages in Chat.js's messageTexts via callbacks.
   * Uses refs to avoid stale closure issues.
   */
  const handleTranscript = useCallback((event) => {
    if (event.type === "user" && event.text) {
      if (turnPhaseRef.current !== "user_speaking") {
        const msgId = uuidv4();
        currentUserMsgIdRef.current = msgId;
        turnPhaseRef.current = "user_speaking";
        addUserMessageRef.current(msgId);
        setActiveStreamingMessageIdRef.current(msgId);
      }
      updateMessageByIdRef.current(currentUserMsgIdRef.current, event.text);
    } else if (event.type === "model" && event.text) {
      if (turnPhaseRef.current !== "ai_speaking") {
        // Finalize any in-progress user message
        if (currentUserMsgIdRef.current) {
          finalizeMessageByIdRef.current(currentUserMsgIdRef.current);
          currentUserMsgIdRef.current = null;
        }
        turnPhaseRef.current = "ai_speaking";
      }
      if (!currentAiMsgIdRef.current) {
        // Bubble not yet created (transcript arrived before isSpeaking effect)
        const msgId = uuidv4();
        currentAiMsgIdRef.current = msgId;
        firstAiTextRef.current = true;
        addAssistantMessageRef.current(msgId);
        setActiveStreamingMessageIdRef.current(msgId);
      }
      if (firstAiTextRef.current) {
        // Replace "Speaking..." placeholder with actual transcript
        replaceMessageByIdRef.current(currentAiMsgIdRef.current, event.text);
        firstAiTextRef.current = false;
      } else {
        updateMessageByIdRef.current(currentAiMsgIdRef.current, event.text);
      }
    } else if (event.type === "turnComplete") {
      if (currentUserMsgIdRef.current) {
        finalizeMessageByIdRef.current(currentUserMsgIdRef.current);
        currentUserMsgIdRef.current = null;
      }
      if (currentAiMsgIdRef.current) {
        finalizeMessageByIdRef.current(currentAiMsgIdRef.current);
        currentAiMsgIdRef.current = null;
      }
      turnPhaseRef.current = "idle";
      firstAiTextRef.current = true;
      setActiveStreamingMessageIdRef.current(null);
    }
  }, []);

  const {
    isSessionActive,
    isConnecting,
    isListening,
    isSpeaking,
    error,
    startSession,
    endSession,
    startListening,
    stopAllAudio,
  } = useGeminiLive();

  // When Addy starts speaking, create an AI bubble with "Speaking..." placeholder
  useEffect(() => {
    if (isSpeaking && !currentAiMsgIdRef.current) {
      // Finalize any in-progress user message
      if (currentUserMsgIdRef.current) {
        finalizeMessageByIdRef.current(currentUserMsgIdRef.current);
        currentUserMsgIdRef.current = null;
      }
      turnPhaseRef.current = "ai_speaking";

      const msgId = uuidv4();
      currentAiMsgIdRef.current = msgId;
      firstAiTextRef.current = true;
      addAssistantMessageRef.current(msgId);
      setActiveStreamingMessageIdRef.current(msgId);
      updateMessageByIdRef.current(msgId, "Speaking...");
    }
  }, [isSpeaking]);

  /**
   * Build voice-optimized system prompt
   */
  const buildSystemPrompt = useCallback(() => {
    let prompt = `You are Addy, an expert AI assistant for loan officers. Your name is Addy — never refer to yourself as Gemini, Google, or any other name. You help with loan processing, document review, pipeline management, and answering questions about loans and borrowers.

You are in a real-time voice conversation. Follow these rules:
- Keep responses concise and conversational — speak naturally
- Do not use markdown, bullet points, numbered lists, or any formatting — speak in plain sentences
- Do not read URLs, file paths, or technical identifiers aloud — instead say things like "I found the document" or "I can see that information"
- When using tools, briefly tell the user what you're doing (e.g., "Let me look that up for you")
- If a tool returns a lot of data, summarize the key points rather than reading everything
- Be warm, professional, and helpful
- Never reveal your underlying model, technology, or that you are powered by Google or Gemini

Tool usage guidelines — follow these carefully:
- To see all tasks for a loan, call get_tasks_for_loan first. This gives you task titles, statuses, and categories.
- To get detailed analysis for a specific task, call get_task_details_for_loan. Use the taskTitles parameter for searching by task name (substring match). For example, to find income tasks use taskTitles: "income", for credit use taskTitles: "credit".
- The ausCategories parameter filters by AUS category labels: "Income/Employment", "Credit", "Asset", "Property", "Disclosures", "Borrower Info". Use these when the user asks about a category.
- Do NOT use categoryTitles to search for task names — categoryTitles matches parent category names like "Borrower Approval", not individual task names.
- When you need the full AI analysis result for a task, pass includeFullContent: true to get_task_details_for_loan.
- Always pass the organizationId and loanId from the context above.`;

    if (organizationIdRef.current) {
      prompt += `\n\nContext:\n- Organization ID: ${organizationIdRef.current}`;
    }
    if (uidRef.current) prompt += `\n- User ID: ${uidRef.current}`;
    if (email) prompt += `\n- User email: ${email}`;
    if (name) prompt += `\n- User name: ${name}`;
    if (loanIdRef.current) {
      prompt += `\n- Current loan ID: ${loanIdRef.current}
- The user is currently viewing this loan. Use this loanId for loan-specific queries unless they specify otherwise.`;
    }

    // Include prior chat history so voice mode has full context
    const history = chatHistoryRef.current;
    if (history && history.length > 0) {
      // Take the last 20 messages to keep prompt size reasonable
      const recent = history.slice(-20);
      const transcript = recent
        .map((msg) => {
          const role = msg.type === "user" ? "User" : "Addy";
          const text = (msg.message || "").trim();
          return text ? `${role}: ${text}` : null;
        })
        .filter(Boolean)
        .join("\n");

      if (transcript) {
        prompt += `\n\nPrior chat conversation (for context — the user is now switching to voice):\n${transcript}`;
      }
    }

    return prompt;
  }, [email, name]);

  /**
   * Play a warm activation chime when entering voice mode (3-note ascending chord)
   */
  const playActivationSound = useCallback(() => {
    try {
      const ctx = new AudioContext();
      const t = ctx.currentTime;

      const playNote = (freq, start, duration, vol) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        osc.connect(gain);
        gain.connect(ctx.destination);
        gain.gain.setValueAtTime(0, t + start);
        gain.gain.linearRampToValueAtTime(vol, t + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, t + start + duration);
        osc.start(t + start);
        osc.stop(t + start + duration);
      };

      // Warm ascending triad — C5, E5, G5 with staggered timing
      playNote(523, 0, 0.25, 0.12);     // C5
      playNote(659, 0.07, 0.22, 0.10);  // E5
      playNote(784, 0.14, 0.28, 0.08);  // G5

      // Close context after notes finish
      setTimeout(() => ctx.close(), 500);
    } catch (e) {
      // Ignore audio errors — sound is non-critical
    }
  }, []);

  /**
   * Start voice session: fetch MCP tools → start Gemini session → start listening
   */
  const startVoice = useCallback(async () => {
    try {
      playActivationSound();
      // Fetch MCP tools
      let mcpTools = [];
      try {
        const url = `${GEMINI_LIVE_URL}/mcp-tools${organizationIdRef.current ? `?organizationId=${organizationIdRef.current}` : ""}`;
        const response = await fetch(url);
        const data = await response.json();
        if (data.success && data.tools) {
          console.log(`useVoiceSession: Loaded ${data.tools.length} MCP tools`);
          mcpTools = data.tools;
        }
      } catch (err) {
        console.error("useVoiceSession: Error fetching MCP tools:", err);
      }

      // Start Gemini Live session (listening auto-starts via useEffect below)
      await startSession({
        voice: "Kore",
        systemInstruction: buildSystemPrompt(),
        tools: mcpTools.length > 0 ? mcpTools : undefined,
        onToolCall: handleToolCall,
        onTranscript: handleTranscript,
        onError: (err) => {
          console.error("useVoiceSession error:", err);
        },
        onSessionClosed: () => {
          console.log("useVoiceSession: Session closed");
        },
      });
    } catch (err) {
      console.error("useVoiceSession: Failed to start voice session:", err);
    }
  }, [startSession, buildSystemPrompt, handleToolCall, handleTranscript, playActivationSound]);

  // Auto-start listening once session becomes active
  useEffect(() => {
    if (isSessionActive && !isListening) {
      startListening();
    }
  }, [isSessionActive, isListening, startListening]);

  /**
   * End voice session: finalize in-progress messages, end Gemini session
   */
  const endVoice = useCallback(async () => {
    // Finalize any in-progress messages
    if (currentUserMsgIdRef.current) {
      finalizeMessageByIdRef.current(currentUserMsgIdRef.current);
      currentUserMsgIdRef.current = null;
    }
    if (currentAiMsgIdRef.current) {
      finalizeMessageByIdRef.current(currentAiMsgIdRef.current);
      currentAiMsgIdRef.current = null;
    }
    turnPhaseRef.current = "idle";
    setActiveStreamingMessageIdRef.current(null);
    setToolStatus(null);

    await endSession();
  }, [endSession]);

  return {
    isVoiceActive: isSessionActive,
    isConnecting,
    isSpeaking,
    isListening,
    error,
    toolStatus,
    startVoice,
    endVoice,
    stopAudio: stopAllAudio,
  };
}
